export default class Cache {
    /**
     * Cache constructor
     * @param {string} type The type of storage to use (local or session).
     */
    constructor(type = 'local') {
        switch (type) {
            case 'local':
                this._storage = window.localStorage;
                break;
            case 'session':
                this._storage = window.sessionStorage;
                break;
            default:
                throw new Error(`Unexpected cache type: ${type}`);
        }
    }

    /**
     * Check if an item has been cached.
     * @param {string} key The key to search for.
     * @returns {boolean} A boolean indicating if the item is in the cache.
     */
    hasItem(key) {
        return this.getItem(key) !== null;
    }

    /**
     * Checks if all items in an array have been cached.
     *
     * @param {Array} keys The keys to search for.
     * @returns {boolean} A boolean indicating if the items are cached.
     */
    hasItems(keys) {
        for (let i = 0; i < keys.length; i++) {
            if (this.getItem(keys[i]) === null) {
                return false;
            }
        }

        return true;
    }

    /**
     * Returns all items that have been cached.
     *
     * @returns {Array}
     */
    getAll() {
        const items = [];
        const keys = Object.getOwnPropertyNames(this._storage);
        let i = keys.length;

        while (i--) {
            items.push(this.getItem(keys[i]));
        }

        return items;
    }

    /**
     * Returns the item under the specified key.
     * @param {string} key The key to search for.
     * @returns {*}
     */
    getItem(key) {
        const value = this._storage.getItem(key);
        return value && JSON.parse(value);
    }

    /**
     * Returns an array of items matching the keys.
     *
     * @param {Array} keys The keys to search for.
     * @returns {Array}
     */
    getItems(keys) {
        const items = [];

        keys.forEach((key) => {
            let item = this.getItem(key);

            if (item !== null) {
                items.push(item);
            }
        });

        return items;
    }

    /**
     * Set an item in the cache.
     *
     * @param {string} key
     * @param {*} val
     */
    setItem(key, val) {
        this._storage.setItem(key, JSON.stringify(val));
    }

    /**
     * Sets multiple items in the cache. Each property name is used as
     * the key, and the property value is used as the item.
     *
     * @param {Object} data
     */
    setItems(data) {
        Object.getOwnPropertyNames(data).forEach((key) => {
            this.setItem(key, data[key]);
        });
    }

    /**
     * Removed an item from the cache at the specified key.
     *
     * @param {string} key
     */
    removeItem(key) {
        this._storage.removeItem(key);
    }

    /**
     * Clears all items in the cache.
     */
    clear() {
        this._storage.clear();
    }
}